import React from "react";
import { Link } from "react-router-dom";
import { Clapperboard, ArrowLeft } from "lucide-react";

/**
 * Public privacy policy. Kept intentionally plain-styled (no gradients, no
 * motion) so recipients from an email footer or SMS can read it on any
 * device without a surprise.
 */
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0D0F12] text-gray-200">
      <header className="border-b border-[#28303F] bg-[#14181F]">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white" data-testid="privacy-logo-link">
            <Clapperboard className="text-amber-400" size={20} />
            <span className="font-[Outfit] font-bold text-lg">Get2Set</span>
          </Link>
          <Link to="/login" className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-200">
            <ArrowLeft size={13} /> Back to sign in
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="privacy-page">
        <h1 className="text-3xl font-extrabold font-[Outfit] text-white mb-2">Privacy Policy</h1>
        <p className="text-xs text-gray-500 uppercase tracking-wider mb-8">Last updated: February 2026</p>

        <p className="mb-6 leading-relaxed">
          Get2Set is a service of Forced Perspective Productions LLC, which operates and offers it under the DBA "Get2Set" ("Get2Set", "we", "us", or "our").
          This Privacy Policy explains how we collect, use, and disclose information when you use the Get2Set platform
          at <a className="text-amber-300 hover:text-amber-200 underline underline-offset-2" href="https://get2set.net">get2set.net</a> or receive
          communications we send on behalf of a production you are associated with.
        </p>

        <Section title="1. Who this policy covers">
          <p>This policy applies to two groups of people:</p>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li><strong>Account holders</strong> — producers, UPMs, 1st ADs, coordinators, and other crew who sign up for Get2Set to create and distribute call sheets.</li>
            <li><strong>Recipients</strong> — cast, crew, clients, vendors, and other third parties who receive a call sheet from an account holder via email or SMS.</li>
          </ul>
        </Section>

        <Section title="2. Information we collect">
          <h3 className="font-semibold text-white mt-3 mb-1">From account holders</h3>
          <ul className="list-disc list-inside space-y-1">
            <li>Name, email address, and password hash (we never store plaintext passwords).</li>
            <li>Production and call-sheet data you enter: crew contacts, locations, schedules, notes, logos, accent colors.</li>
            <li>Payment and subscription data processed by our payment partner, Stripe. We do not store full card numbers.</li>
            <li>Technical data: IP address, browser, session cookies, timestamps of key actions.</li>
          </ul>
          <h3 className="font-semibold text-white mt-4 mb-1">From recipients</h3>
          <ul className="list-disc list-inside space-y-1">
            <li>The name, phone number, and email address an account holder provides when adding you to their crew database or call sheet.</li>
            <li>Delivery and engagement events for messages addressed to you (sent, delivered, opened, bounced, replied, confirmed/declined RSVP).</li>
          </ul>
        </Section>

        <Section title="3. Email and SMS communications">
          <p>
            Get2Set sends email and SMS messages on behalf of productions to the recipients those productions have added to a call sheet.
            Messages are transactional and relate to a specific production or shoot you have been invited to.
          </p>
          <h3 className="font-semibold text-white mt-3 mb-1">Email</h3>
          <ul className="list-disc list-inside space-y-1">
            <li>Email is delivered through our email service provider, SendGrid (Twilio Inc.).</li>
            <li>Every email includes the sending production's name and a reply-to address so you can respond directly.</li>
            <li>You may opt out of future call-sheet emails from a production by replying with "UNSUBSCRIBE" or by contacting the production coordinator who added you. If you believe you were added without consent, email <a className="text-amber-300 underline underline-offset-2" href="mailto:support@get2set.net">support@get2set.net</a> and we will remove you from the sender's crew database.</li>
          </ul>
          <h3 className="font-semibold text-white mt-3 mb-1">SMS</h3>
          <ul className="list-disc list-inside space-y-1">
            <li>SMS is delivered through Twilio and sent only to phone numbers an account holder has entered.</li>
            <li>SMS from Get2Set are informational and relate only to a call-sheet distribution. We will not send marketing SMS.</li>
            <li><strong>Reply STOP to opt out of SMS from a production at any time.</strong> Reply HELP for help. Message frequency varies by production.</li>
            <li>Standard message and data rates may apply from your carrier.</li>
            <li>Get2Set does not sell SMS numbers or share them with advertisers.</li>
          </ul>
        </Section>

        <Section title="4. How we use information">
          <ul className="list-disc list-inside space-y-1">
            <li>To operate the Get2Set platform: authenticate accounts, generate PDF call sheets, deliver email and SMS, track delivery and RSVP status.</li>
            <li>To bill, renew, and manage subscriptions through Stripe.</li>
            <li>To respond to support requests and enforce our Terms & Conditions.</li>
            <li>To improve the service (anonymized, aggregated usage).</li>
            <li>To comply with legal obligations.</li>
          </ul>
        </Section>

        <Section title="5. How we share information">
          <p>We share information only with the service providers required to run the platform:</p>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li><strong>SendGrid</strong> (email delivery)</li>
            <li><strong>Twilio</strong> (SMS delivery)</li>
            <li><strong>Stripe</strong> (payment processing)</li>
            <li><strong>MongoDB Atlas</strong> (encrypted database hosting)</li>
            <li><strong>OpenStreetMap / Photon / Open-Meteo / NWS</strong> (geocoding and weather lookups for call-sheet fields; we send only the location string you entered)</li>
          </ul>
          <p className="mt-3">We do not sell personal information. We will not share your data for advertising.</p>
        </Section>

        <Section title="6. Data retention">
          <p>
            Account data is retained for as long as your account is active and for a reasonable period thereafter for legal, tax, and accounting purposes.
            You may request deletion by emailing <a className="text-amber-300 underline underline-offset-2" href="mailto:support@get2set.net">support@get2set.net</a>.
            Recipients may request removal from a production's crew database at any time.
          </p>
        </Section>

        <Section title="7. Your rights">
          <p>Depending on where you live (including under GDPR and CCPA), you may have rights to:</p>
          <ul className="list-disc list-inside space-y-1 mt-2">
            <li>Access, correct, or delete your personal information.</li>
            <li>Object to or restrict processing.</li>
            <li>Port a copy of your data.</li>
            <li>Lodge a complaint with your local supervisory authority.</li>
          </ul>
          <p className="mt-3">To exercise any of these rights, email <a className="text-amber-300 underline underline-offset-2" href="mailto:support@get2set.net">support@get2set.net</a>.</p>
        </Section>

        <Section title="8. Children">
          <p>Get2Set is not directed to anyone under 16. We do not knowingly collect personal information from children.</p>
        </Section>

        <Section title="9. Changes to this policy">
          <p>
            We may update this policy from time to time. Material changes will be announced in-app or by email to account holders.
            The "Last updated" date at the top of this page reflects the current version.
          </p>
        </Section>

        <Section title="10. Contact">
          <p>
            Forced Perspective Productions LLC d/b/a Get2Set<br />
            Email: <a className="text-amber-300 underline underline-offset-2" href="mailto:support@get2set.net">support@get2set.net</a>
          </p>
        </Section>

        <p className="mt-10 text-xs text-gray-500">
          See also our <Link to="/terms" className="text-amber-300 underline underline-offset-2" data-testid="privacy-terms-link">Terms &amp; Conditions</Link>.
        </p>
      </main>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mb-6 leading-relaxed">
      <h2 className="text-lg font-semibold font-[Outfit] text-white mb-2">{title}</h2>
      <div className="text-sm text-gray-300 space-y-2">{children}</div>
    </section>
  );
}
